import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, Trophy, Building2, ArrowRight, ArrowLeft, CheckCircle2,
  Waves, GraduationCap, Briefcase, Heart, Megaphone, Users2,
  Palette, Scroll, Coffee, Home
} from 'lucide-react';
import { Button, Badge } from '../components/ui';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { SEO } from '../components/SEO';

interface UseCaseConfig {
  id: string;
  icon: React.ElementType;
  title: string;
  from: string;
  to: string;
  target: string;
  benefit: string;
  details: string[];
  toolLink?: string;
  toolLabel?: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  faqs: { q: string; a: string }[];
  relatedCases: string[];
}

const USE_CASE_CONFIGS: Record<string, UseCaseConfig> = {
  'hoa': {
    id: 'hoa',
    icon: Home,
    title: "From HOA Burnout to Harmonious Living",
    from: "Board meetings dominated by disputes, paper checks lost in the mail, and undocumented covenants.",
    to: "Automated assessment ledgers, a public digital vault for bylaws, and transparent, tamper-evident audit trails.",
    target: "Homeowners Associations, Condo Boards, Tenant Unions",
    benefit: "Eliminate the administrative friction of managing your community. Automate dues collection, secure covenants, and build resident trust with absolute clarity.",
    details: [
      "Automated Assessment Ledgers: Residents pay online via secure recurring bank transfers or card payments.",
      "Centralized Vault Archive: Instant access for residents to bylaws, covenants, and approved meeting minutes.",
      "Tamper-Evident Audits: Permanent logs of every transaction protecting the board from liability and disputes."
    ],
    toolLink: '/tools/hoa-budget-tracker',
    toolLabel: 'Try the HOA Budget Tracker',
    seoTitle: "HOA Management Software | Automate Dues, Bylaws & Audits",
    seoDescription: "Simplify HOA management with OrgSets. Automate assessment collection, store bylaws in a secure digital vault, and generate tamper-evident audit trails. Free HOA budget tracker included.",
    seoKeywords: "hoa management software, hoa budgeting tools, hoa dues collection online, homeowner association management, condo board management software, hoa violation tracking",
    faqs: [
      { q: "What is HOA management software?", a: "HOA management software automates dues collection, covenant storage, meeting minutes, and resident communications for homeowner and condo associations — replacing paper processes and spreadsheets." },
      { q: "Can OrgSets handle automated HOA dues collection?", a: "Yes. OrgSets connects to Stripe for recurring online payments, automatically splits assessments across reserve and operating budgets, and generates tamper-evident receipts." },
      { q: "How does OrgSets store HOA bylaws and covenants?", a: "All documents are stored in a secure Digital Vault hosted on SOC 2 & ISO 27001 certified AWS infrastructure with version history, resident access controls, and automatic distribution when updates are made." },
      { q: "Is there a free HOA tool I can try?", a: "Yes. OrgSets offers a free HOA Budget Tracker that lets you plan operating and reserve budgets with visual forecasting — no signup required." }
    ],
    relatedCases: ['senior', 'nonprofit']
  },
  'swim-teams': {
    id: 'swim-teams',
    icon: Waves,
    title: "From Scattered Swimmers to Seeded Meets",
    from: "Messy entry sheets, manual lane seeding, and parent volunteer chaos on paper.",
    to: "Automated spearhead lane seeding, instant digital heat sheets, and real-time coordinator alerts.",
    target: "Swim Clubs, Summer Leagues, Swim Coordinators",
    benefit: "Coordinate swimmer entries, automate seed times and heat sheet generation, and assign volunteer roles seamlessly for every swim meet.",
    details: [
      "Automated Heat Seeding: Generate perfect heat sheets with standard spearhead lane ordering in seconds.",
      "Parent & Volunteer Portals: Let parents track entries, sign waivers, and sign up for timer/marshal shifts.",
      "Real-Time Broadcasts: Send heat starts and emergency weather alerts instantly to all parents' phones."
    ],
    toolLink: '/tools/swim-heat-sheet-generator',
    toolLabel: 'Try the Swim Heat Sheet Generator',
    seoTitle: "Swim Team Management Software | Heat Sheets & Meet Coordination",
    seoDescription: "Manage your swim team or summer league with OrgSets. Automated heat sheet generation, spearhead lane seeding, parent volunteer portals, and real-time meet alerts. Free heat sheet tool included.",
    seoKeywords: "swim team management software, swim meet heat sheet generator, swim meet management, swim team seed times, summer league swim coordinator, swim heat sheet template, what is seed time in swimming",
    faqs: [
      { q: "What is a seed time in swimming?", a: "A seed time is a swimmer's best previously recorded time for a specific event, used to assign them to heats and lanes so races are competitively balanced." },
      { q: "How does OrgSets generate swim heat sheets?", a: "Enter your swimmers' seed times and event entries, and OrgSets automatically generates heat sheets using standard spearhead lane seeding — fastest swimmers in center lanes, balanced across heats." },
      { q: "Can parents sign up for volunteer shifts through OrgSets?", a: "Yes. OrgSets provides dedicated parent portals where families can view their swimmer's entries, sign digital waivers, and sign up for timer, marshal, and concession shifts." },
      { q: "Is the swim heat sheet generator free?", a: "Yes. The standalone Swim Heat Sheet Generator tool is completely free and requires no signup. For full team management, OrgSets offers paid plans." }
    ],
    relatedCases: ['sports-leagues', 'senior']
  },
  'sports-leagues': {
    id: 'sports-leagues',
    icon: Trophy,
    title: "From Local Team to Regional League",
    from: "Lost clipboards and physical sign-up sheets.",
    to: "A streamlined home to manage your whole league with clarity.",
    target: "Youth Sports, Fitness Groups, Esports",
    benefit: "Focus on the game, not the paperwork. We help you manage your teams and members with ease.",
    details: [
      "Simple Registration: Easy sign-ups for players and parents.",
      "Safe & Secure: Keep all your important documents and waivers in one private vault.",
      "Clear Goals: Track your team's progress and fundraising together."
    ],
    toolLink: '/tools/tournament-bracket-generator',
    toolLabel: 'Try the Tournament Bracket Generator',
    seoTitle: "Sports League Management Software | Registration, Scheduling & Dues",
    seoDescription: "Run your youth sports league, rec league, or esports organization with OrgSets. Online registration, automated dues, tournament brackets, and team communications in one platform.",
    seoKeywords: "sports league management software, youth sports registration, league scheduling software, sports team management, esports league management, rec league management tool",
    faqs: [
      { q: "How does OrgSets help manage a sports league?", a: "OrgSets handles player registration, team rosters, dues collection, tournament bracket generation, game scheduling, and parent communications — all in one platform." },
      { q: "Can I create tournament brackets with OrgSets?", a: "Yes. OrgSets includes a free interactive Tournament Bracket Generator that supports single-elimination, randomized seeding, and printable exports." },
      { q: "Does OrgSets work for esports leagues?", a: "Absolutely. OrgSets is game-agnostic and works for any organized competition — youth sports, rec leagues, and esports tournaments alike." }
    ],
    relatedCases: ['swim-teams', 'hobbyist']
  },
  'nonprofit': {
    id: 'nonprofit',
    icon: Heart,
    title: "From Local Volunteers to National Impact",
    from: "Fundraising efforts that get lost between layers.",
    to: "A clear view of your mission's impact and easy coordination everywhere.",
    target: "Charities, NGOs, Advocacy Groups",
    benefit: "Grow your mission from one city to every state without losing the personal touch that makes your volunteers special.",
    details: [
      "Clear Impact Tracking: See exactly how every contribution helps your mission grow.",
      "Shared Goals: Work together on big targets, from local events to national campaigns.",
      "Volunteer Connection: Reach your supporters in seconds when they're needed most."
    ],
    toolLink: '/tools/volunteer-value-calculator',
    toolLabel: 'Try the Volunteer Value Calculator',
    seoTitle: "Nonprofit Management Platform | Volunteers, Dues & Governance",
    seoDescription: "Manage your nonprofit, charity, or NGO with OrgSets. Volunteer tracking, dues collection, board governance, and multi-chapter coordination. Free volunteer value calculator included.",
    seoKeywords: "nonprofit management software, volunteer management platform, charity management tool, ngo management software, nonprofit dues collection, volunteer value calculator",
    faqs: [
      { q: "How does OrgSets help nonprofits?", a: "OrgSets centralizes volunteer management, fundraising tracking, board governance, document storage, and multi-chapter coordination — replacing scattered spreadsheets and email chains." },
      { q: "Can OrgSets handle multi-chapter nonprofit structures?", a: "Yes. OrgSets uses hierarchical nodes so national HQs can oversee state and local chapters, with automated dues splitting and policy distribution at every level." },
      { q: "Is there a free tool for calculating volunteer value?", a: "Yes. The Volunteer Value Calculator converts volunteer hours to dollar equivalents using Independent Sector rates — free, no signup required." }
    ],
    relatedCases: ['advocacy', 'heritage']
  },
  'alumni': {
    id: 'alumni',
    icon: GraduationCap,
    title: "Graduates to Endowment Network",
    from: "Mailing lists and LinkedIn groups that nobody checks.",
    to: "A verified portal for mentoring, giving, and career tracking.",
    target: "Alumni Associations, Bootcamp Grads, Company Retirees",
    benefit: "Turn nostalgic connections into a powerful, self-sustaining endowment engine.",
    details: [
      "Mentorship Matching: Connect senior alumni with newcomers.",
      "Endowment Ledger: Transparent tracking of scholarship funds.",
      "Global Directory: Search members by industry, year, or location."
    ],
    toolLink: '/tools/alumni-mentorship-match',
    toolLabel: 'Try the Alumni Mentorship Matcher',
    seoTitle: "Alumni Association Management Software | Mentoring & Endowments",
    seoDescription: "Build and manage your alumni network with OrgSets. Mentorship matching, endowment tracking, global directories, and class reunion planning. Free alumni tools included.",
    seoKeywords: "alumni association management, alumni network software, alumni mentorship platform, alumni endowment tracking, class reunion planning software",
    faqs: [
      { q: "How does OrgSets help alumni associations?", a: "OrgSets provides verified member directories, mentorship matching, endowment tracking, event coordination, and class reunion tools — all in a secure, branded portal." },
      { q: "Can alumni search by industry or graduation year?", a: "Yes. The global directory supports filtering by industry, graduation year, location, and custom fields defined by your association." }
    ],
    relatedCases: ['professional', 'social-clubs']
  },
  'social-clubs': {
    id: 'social-clubs',
    icon: Users2,
    title: "Social Club to Structured Order",
    from: "Dues paid in cash at the bar and paper meeting minutes.",
    to: "Digital dues split across national/local ledgers with voting.",
    target: "Fraternities, Service Clubs, Private Societies",
    benefit: "Preserve legacy traditions while modernizing the management of growth.",
    details: [
      "Dues Splitting: Automatic distribution to local and national pots.",
      "Digital Minutes: Secure, searchable archive of all board meetings.",
      "Election Guard: Anonymous, cryptographic leadership elections."
    ],
    toolLink: '/tools/dues-split-calculator',
    toolLabel: 'Try the Dues Split Calculator',
    seoTitle: "Fraternity & Social Club Management Software | Dues & Elections",
    seoDescription: "Manage your fraternity, sorority, or social club with OrgSets. Automated dues splitting, digital meeting minutes, anonymous elections, and chapter coordination. Free dues calculator included.",
    seoKeywords: "fraternity management software, sorority management tool, social club management, fraternity dues collection, chapter management software, greek life management",
    faqs: [
      { q: "Can OrgSets manage fraternity or sorority dues?", a: "Yes. OrgSets automates dues collection with Stripe, splits payments between national and local chapter ledgers, and provides transparent financial reporting." },
      { q: "Does OrgSets support anonymous elections?", a: "Yes. Election Guard provides cryptographic, anonymous voting for leadership elections with tamper-evident audit trails." }
    ],
    relatedCases: ['alumni', 'heritage']
  },
  'heritage': {
    id: 'heritage',
    icon: Scroll,
    title: "Lineage Society to National Archive",
    from: "Fragmented genealogy proofs spread across regional curators and paper folders.",
    to: "3-Tiered ledger system (National-State-Local) with an encrypted Lineage Vault.",
    target: "Heritage Societies, Lineage Groups, Historical Chapters",
    benefit: "Protect your historical integrity while automating the complex dues split across all levels of your organization.",
    details: [
      "3-Tier Ledger: Automatically distribute National, State, and Local dues in one click.",
      "Lineage Vault: Hosted on SOC 2 & ISO 27001 certified AWS infrastructure with 256-bit encryption for genealogical proofs and historical charters.",
      "Global Policies: Send bylaws and ceremonial protocols instantly from National HQ to every local chapter."
    ],
    toolLink: '/tools/bylaws-templates-builder',
    toolLabel: 'Try the Bylaws Templates Builder',
    seoTitle: "Heritage Society Management Software | 3-Tier Dues & Lineage Vault",
    seoDescription: "Manage your heritage society, lineage group, or historical chapter with OrgSets. 3-tier dues distribution, encrypted genealogy vault, and national-to-local policy distribution.",
    seoKeywords: "heritage society management, lineage society software, genealogy society management, historical society dues, heritage organization management",
    faqs: [
      { q: "How does OrgSets handle 3-tier dues for heritage societies?", a: "OrgSets automatically splits each member's dues payment across National, State, and Local chapter ledgers in one transaction, with transparent reporting at every level." },
      { q: "Is the Lineage Vault secure?", a: "Yes. All documents are hosted on SOC 2 & ISO 27001 certified AWS infrastructure with 256-bit encryption, access controls, and version history. Only authorized curators can manage sensitive genealogical proofs." }
    ],
    relatedCases: ['social-clubs', 'nonprofit']
  },
  'hobbyist': {
    id: 'hobbyist',
    icon: Sparkles,
    title: "From a Hobby to a Community",
    from: "Messy group chats and lost Facebook posts.",
    to: "A clear, private home where everything is easy to find.",
    target: "Book Clubs, Gaming Groups, Collectors",
    benefit: "Whether you're starting a small club or a growing hobby group, we help you keep everyone connected without the stress of managing multiple apps.",
    details: [
      "Private Member List: A simple, secure list so you can easily reach out to friends without digging through old chats.",
      "Shared Resource Library: A central home for all your group's treasures\u2014lists, guides, and plans.",
      "Friendly Notifications: Important updates stay at the top so everyone knows what's happening."
    ],
    toolLink: '/tools/sign-up-sheet-generator',
    toolLabel: 'Try the Free Sign-Up Sheet Generator',
    seoTitle: "Community Group Management for Book Clubs, Gaming & Hobby Groups",
    seoDescription: "Organize your book club, gaming group, or collector community with OrgSets. Member directories, shared resources, event scheduling, and group notifications. Free sign-up sheet generator included.",
    seoKeywords: "book club management, hobby group management software, gaming group organizer, collector community platform, community group management",
    faqs: [
      { q: "Can OrgSets manage a small hobby group?", a: "Yes. OrgSets works for groups of any size, from a 5-person book club to a 500-member collector community. The free tier covers small groups." },
      { q: "Do I need technical skills to set up OrgSets?", a: "No. Create your org, invite members, and start organizing in minutes. No coding or technical setup required." }
    ],
    relatedCases: ['senior', 'social-clubs']
  },
  'business': {
    id: 'business',
    icon: Building2,
    title: "From a Single Store to a Global Franchise",
    from: "Different ways of working at every location and messy group chats.",
    to: "One beautiful home for your whole brand, with local pride.",
    target: "Retail, Franchises, Professional Services",
    benefit: "Keep everyone connected to the big picture while giving local owners the tools they need to shine in their community.",
    details: [
      "Brand Harmony: A beautiful, consistent look for every single location.",
      "Instant Updates: Reach every member of your team across the world in seconds.",
      "Role-Based Access: Simple tools to manage who sees what at every level."
    ],
    toolLink: '/tools/membership-certificate',
    toolLabel: 'Try the Membership Certificate Generator',
    seoTitle: "Franchise & Multi-Location Business Management Platform",
    seoDescription: "Manage your franchise, multi-location business, or professional services network with OrgSets. Unified branding, role-based access, and instant team communications across all locations.",
    seoKeywords: "franchise management software, multi location business management, franchise communication platform, business network management",
    faqs: [
      { q: "How does OrgSets help manage multiple business locations?", a: "OrgSets uses hierarchical nodes so headquarters can manage brand standards, role-based permissions, and communications across all franchise locations from a single dashboard." }
    ],
    relatedCases: ['professional', 'creative']
  },
  'professional': {
    id: 'professional',
    icon: Briefcase,
    title: "Independent Pros to Accredited Body",
    from: "Manual certification tracking and fragmented email threads.",
    to: "Automated credentialing with a transparent ethics ledger.",
    target: "Trade Unions, Medical Societies, Professional Guilds",
    benefit: "Establish authority in your industry with standardized rules for every member.",
    details: [
      "Credential Hosting: Store and verify professional certifications.",
      "Ethics Vault: Securely manage grievances and disciplinary records.",
      "Direct Upstream Comms: Policy updates travel straight to policy-makers."
    ],
    toolLink: '/tools/certification-pricing-calculator',
    toolLabel: 'Try the Certification Pricing Calculator',
    seoTitle: "Professional Association Management | Credentialing & Governance",
    seoDescription: "Manage your professional association, trade union, or guild with OrgSets. Certification tracking, ethics management, policy distribution, and standards enforcement.",
    seoKeywords: "professional association management software, trade union management, guild management platform, certification tracking software, professional credentialing",
    faqs: [
      { q: "Can OrgSets track professional certifications?", a: "Yes. Members can upload credentials, and administrators can verify, track expiration dates, and send renewal reminders automatically." },
      { q: "How does OrgSets handle ethics management?", a: "The Ethics Vault provides secure, auditable storage for grievances and disciplinary records with role-based access controls." }
    ],
    relatedCases: ['alumni', 'business']
  },
  'advocacy': {
    id: 'advocacy',
    icon: Megaphone,
    title: "Grassroots Action to Policy Powerhouse",
    from: "Viral social posts that don't translate into local policy changes.",
    to: "Strict governance tiers for handling sensitive strategic blueprints.",
    target: "Political Movements, Union Organizing, Environmental Groups",
    benefit: "Turn digital energy into a disciplined organization that wins.",
    details: [
      "Voter Vetting: Ensure your voting body is verified and eligible.",
      "Strategy Blueprints: Securely store campaign plans in the Vault.",
      "Mass Mobilization: Broadcast critical instructions to field organizers."
    ],
    toolLink: '/tools/canvassing-route-optimizer',
    toolLabel: 'Try the Canvassing Route Optimizer',
    seoTitle: "Community Advocacy & Grassroots Organizing Platform",
    seoDescription: "Organize your advocacy group, political movement, or environmental campaign with OrgSets. Voter vetting, secure strategy vaults, mass mobilization tools, and canvassing optimization.",
    seoKeywords: "community organizing software, grassroots organizing platform, advocacy group management, political campaign management, community organizing database",
    faqs: [
      { q: "Can OrgSets help with grassroots organizing?", a: "Yes. OrgSets provides voter vetting, secure strategy document storage, mass broadcast tools, and canvassing route optimization for political movements and advocacy groups." },
      { q: "Is OrgSets secure enough for political campaigns?", a: "Yes. OrgSets uses 256-bit AES encryption on SOC 2-certified infrastructure, role-based access controls, and tamper-evident audit trails to protect sensitive strategic documents." }
    ],
    relatedCases: ['nonprofit', 'social-clubs']
  },
  'creative': {
    id: 'creative',
    icon: Palette,
    title: "Freelance Circle to Global Studio",
    from: "Siloed portfolios and disjointed project management.",
    to: "Unified studio branding with hierarchical node accountability.",
    target: "Design Collectives, Production Houses, Music Labels",
    benefit: "Collaborate at scale while protecting each studio's creative autonomy.",
    details: [
      "Studio Referrals: Incentivize growth through a referral engine.",
      "Asset Vault: Share high-value creative assets across permissions.",
      "Project Ledgers: Track revenue splits on collaborative projects."
    ],
    toolLink: '/tools/sponsorship-package-pricer',
    toolLabel: 'Try the Sponsorship Package Pricer',
    seoTitle: "Creative Studio & Design Collective Management Platform",
    seoDescription: "Manage your design collective, production house, or music label with OrgSets. Unified branding, asset vault, project revenue splitting, and studio referral tracking.",
    seoKeywords: "creative studio management, design collective software, production house management, music label management, creative agency platform",
    faqs: [
      { q: "How does OrgSets help creative collectives?", a: "OrgSets provides unified studio branding, asset sharing with granular permissions, project revenue splitting, and referral tracking — so studios can collaborate at scale while maintaining creative autonomy." }
    ],
    relatedCases: ['business', 'professional']
  },
  'senior': {
    id: 'senior',
    icon: Coffee,
    title: "From a Noticeboard to a Vibrant Social Scene",
    from: "Flyers in the hallway, forgotten game schedules, and missed connections.",
    to: "A lively, accessible community organization for activity scheduling and finding local friends.",
    target: "Senior Living, Retirement Communities, Active Adult Centers",
    benefit: "Keep residents engaged, active, and socially connected with accessible scheduling and interest-matching.",
    details: [
      "Activity Calendar: Easy online scheduling and sign-ups for Bingo, water aerobics, and weekly Bridge tournaments.",
      "Social Directory: Find neighbors who share your interests for a game of Canasta, shuffleboard, or a morning walk.",
      "Clear Communication: High-contrast, easy-to-read daily announcements and newsletters."
    ],
    toolLink: '/tools/bingo-caller-board',
    toolLabel: 'Try the Bingo Caller Board',
    seoTitle: "Senior Living Community Management Software | Activities & Social",
    seoDescription: "Keep your senior living community engaged with OrgSets. Activity scheduling, interest-based social directories, accessible newsletters, and Bingo caller boards. Free Bingo tool included.",
    seoKeywords: "senior living management software, retirement community management, senior activity scheduling, senior living social platform, active adult community software",
    faqs: [
      { q: "Is OrgSets accessible for seniors?", a: "Yes. OrgSets uses high-contrast design, large readable fonts, and simple navigation. Activity calendars, newsletters, and social directories are designed for ease of use." },
      { q: "Can residents sign up for activities online?", a: "Yes. Residents can browse the activity calendar, sign up with one click, and receive reminders. Staff can manage scheduling, capacity, and waitlists." }
    ],
    relatedCases: ['hoa', 'hobbyist']
  }
};

export const UseCaseDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const config = slug ? USE_CASE_CONFIGS[slug] : null;

  if (!config) {
    return (
      <div className="min-h-screen pt-28 text-center flex flex-col items-center justify-center bg-white dark:bg-[#050505] dark:text-white">
        <SEO title="Use Case Not Found | OrgSets" description="The requested use case does not exist." noindex />
        <h2 className="text-2xl font-black mb-2">Use Case Not Found</h2>
        <p className="text-gray-500 mb-6">The requested use case does not exist.</p>
        <Button onClick={() => navigate('/use-cases')} variant="secondary">Back to Use Cases</Button>
      </div>
    );
  }

  const Icon = config.icon;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": config.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://orgsets.com" },
      { "@type": "ListItem", "position": 2, "name": "Use Cases", "item": "https://orgsets.com/use-cases" },
      { "@type": "ListItem", "position": 3, "name": config.title, "item": `https://orgsets.com/use-cases/${slug}` }
    ]
  };

  const relatedConfigs = config.relatedCases
    .map(id => USE_CASE_CONFIGS[id])
    .filter(Boolean);

  return (
    <div className="min-h-screen font-sans bg-white text-navy-900 dark:text-white dark:bg-[#050505]">
      <SEO
        title={config.seoTitle}
        description={config.seoDescription}
        keywords={config.seoKeywords}
        schema={[faqSchema, breadcrumbSchema]}
      />
      <PublicHeader />

      <main className="pb-24">
        {/* Hero */}
        <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black">
          <div className="p-8 pt-28 md:p-16 md:pt-32 lg:p-12 lg:pt-32 xl:p-24 xl:pt-40 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-6">
              <Link to="/use-cases" className="text-xs font-black uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity">
                Use Cases
              </Link>
              <span className="text-xs opacity-40">/</span>
              <span className="text-xs font-black uppercase tracking-widest opacity-80">{config.target.split(',')[0]}</span>
            </div>
            <Badge variant="blue" className="mb-4 w-fit">{config.target}</Badge>
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-5xl xl:text-6xl 2xl:text-[80px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
              {config.title}
            </h1>
            <p className="text-xl md:text-2xl font-bold leading-snug mb-8">
              {config.benefit}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="h-14 px-10 text-lg font-bold rounded-none border-[4px] border-white dark:border-black bg-white text-black dark:bg-black dark:text-white w-full sm:w-auto uppercase hover:bg-[#FFD600] hover:text-black hover:border-[#FFD600] transition-colors"
                onClick={() => window.location.href = '/sign-up'}
              >
                Get Started Free
              </Button>
              {config.toolLink && (
                <Link
                  to={config.toolLink}
                  className="h-14 px-10 text-lg font-bold rounded-none border-[4px] border-white/30 dark:border-black/30 inline-flex items-center gap-2 uppercase hover:border-white dark:hover:border-black transition-colors"
                >
                  {config.toolLabel} <ArrowRight size={18} />
                </Link>
              )}
            </div>
          </div>
          <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-secondary rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-500 rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
            <div className="relative z-10">
              <div className="border-[4px] border-black dark:border-white bg-[#FFD600] text-black p-6 shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
                <Icon size={80} />
              </div>
            </div>
          </div>
        </section>

        {/* Transformation */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 border-[4px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A]">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#FC3D21] block mb-4 font-mono">The Chaos (Before)</span>
                <p className="text-lg font-bold leading-relaxed">{config.from}</p>
              </div>
              <div className="p-8 border-[4px] border-black dark:border-white bg-[#0044FF] text-white">
                <span className="text-[10px] font-bold uppercase tracking-widest text-green-300 block mb-4 font-mono">The Order (With OrgSets)</span>
                <p className="text-lg font-bold leading-relaxed">{config.to}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-24 px-4 bg-[#F5F5F0] dark:bg-[#0A0A0A] border-y-[8px] border-black dark:border-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-black font-display tracking-tight mb-12 uppercase">What You Get</h2>
            <div className="space-y-8">
              {config.details.map((detail, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-white dark:bg-black border-[2px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
                  <div className="shrink-0 w-8 h-8 bg-green-500/10 text-green-500 flex items-center justify-center border-[2px] border-green-500">
                    <CheckCircle2 size={18} />
                  </div>
                  <p className="text-base font-bold">{detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        {config.faqs.length > 0 && (
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-black font-display tracking-tight mb-12 uppercase">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {config.faqs.map((faq, idx) => (
                  <div key={idx} className="border-l-4 border-[#0044FF] pl-6">
                    <h3 className="text-lg font-black mb-2">{faq.q}</h3>
                    <p className="text-base opacity-80 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Use Cases */}
        {relatedConfigs.length > 0 && (
          <section className="py-16 md:py-24 px-4 bg-[#F5F5F0] dark:bg-[#0A0A0A] border-y-[8px] border-black dark:border-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-black font-display tracking-tight mb-8 uppercase">Related Use Cases</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedConfigs.map(related => {
                  const RelatedIcon = related.icon;
                  return (
                    <Link
                      key={related.id}
                      to={`/use-cases/${related.id}`}
                      className="flex items-start gap-4 p-6 bg-white dark:bg-black border-[2px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#000] transition-all group"
                    >
                      <div className="shrink-0 p-3 bg-[#FFD600] text-black border-[2px] border-black">
                        <RelatedIcon size={24} />
                      </div>
                      <div>
                        <h3 className="text-base font-black mb-1 group-hover:text-brand-primary transition-colors">{related.title}</h3>
                        <p className="text-sm opacity-60">{related.target}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-3xl mx-auto text-center border-[4px] border-black dark:border-white p-8 md:p-12 shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] bg-[#F5F5F0] dark:bg-[#0A0A0A]">
            <h2 className="text-3xl md:text-5xl font-black font-display tracking-tight mb-8 uppercase">
              Ready to get organized?
            </h2>
            <p className="text-xl opacity-80 mb-12 font-bold">
              Join thousands of organizations that have replaced spreadsheets and group chats with OrgSets.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="h-16 px-12 text-xl font-bold rounded-none border-[4px] border-black dark:border-white bg-black text-white dark:bg-white dark:text-black w-full sm:w-auto uppercase hover:bg-[#FFD600] hover:text-black transition-colors" onClick={() => window.location.href = '/sign-up'}>
                Get Started for Free
              </Button>
              <Button variant="ghost" size="lg" className="h-16 px-12 font-black uppercase tracking-widest border-[4px] border-transparent hover:border-black dark:hover:border-white rounded-none" onClick={() => navigate('/use-cases')}>
                View All Use Cases
              </Button>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};
